import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { z } from "zod";
import { useForm } from "@core/features/form";
import {
  useFetchOrganization,
  useUpdateOrganization,
} from "@core/features/organization";
import { organizationNameField } from "@core/schemas/fields";
import { useStepFormSync } from "@platform/features/onboarding";
import type { StepValidationState } from "@platform/features/onboarding";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrgDisplayNameStep } from "./OrgDisplayNameStep";

const schema = z.object({
  name: organizationNameField,
});

export type Props = {
  programId: number;
  organizationId: number;
  onComplete: () => void;
  onSkip?: () => void;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
};

/**
 * Org display name sub-step container.
 * Allows admin to confirm/update the organization display name.
 */
export function OrgDisplayNameStepContainer({
  organizationId,
  onComplete,
  onStepStateChange,
  submitRef,
}: Props) {
  const { data: organization, isLoading } = useFetchOrganization({
    organizationId,
    enabled: !!organizationId,
  });

  const mutation = useUpdateOrganization({
    organizationId,
    onSuccess: () => {
      onComplete();
    },
  });

  const defaultValues = useMemo(
    () => ({
      name: organization?.name ?? "",
    }),
    [organization]
  );

  const { form, handleSubmit } = useForm({
    schema,
    defaultValues,
    onSubmit: mutation.mutateAsync,
  });

  const hasInitialDataLoaded = useRef(false);

  useEffect(() => {
    if (organization && !hasInitialDataLoaded.current) {
      hasInitialDataLoaded.current = true;
      form.reset({
        name: organization.name ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  useStepFormSync({
    shouldSync: true,
    onStepStateChange,
    submitRef,
    formState: form.formState,
    onSubmit: handleSubmit,
    canProceed: form.formState.isValid && !mutation.isPending,
    primaryLabel: "Agree and Continue",
  });

  if (isLoading || !organization) {
    return <Loader isFullPage />;
  }

  return <OrgDisplayNameStep form={form} />;
}

export default OrgDisplayNameStepContainer;
